import { useEffect } from 'react';
import { RouterProvider, createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, App as AntApp, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import { antdTheme } from './theme';
import { queryClient } from './lib/queryClient';
import { useAuthStore } from './stores/authStore';
import { AdminLayout } from './layouts/AdminLayout';
import LoginPage from './pages/login';
import DashboardPage from './pages/dashboard';
import ApprovalsPage from './pages/approvals';
import UsersPage from './pages/users';
import CustomersPage from './pages/customers';
import OrderNotesPage from './pages/order-notes';
import OrderHistoryPage from './pages/order-history';
import MembershipPage from './pages/membership';
import PaymentPage from './pages/payment';
import PaymentTransfersPage from './pages/payment-transfers';
import ReferralsPage from './pages/referrals';
import DiscountCouponsPage from './pages/discount-coupons';
import PromotionLinksPage from './pages/promotion-links';
import PhoneReleasesPage from './pages/phone-releases';
import IdentityMergesPage from './pages/identity-merges';
import AnnualArchivePage from './pages/annual-archive';
import FileCleanupPage from './pages/file-cleanup';
import SettingsPage from './pages/settings';
import RegisterFlowPage from './pages/settings/register-flow';
import CredentialsPage from './pages/settings/credentials';

dayjs.locale('zh-cn');

/** 管理员守卫：未登录跳登录页；登录中显示加载；非 admin 由 _hydrate 已拦截 */
function RequireAdmin() {
  const { user, loading } = useAuthStore();
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Spin size="large" tip="正在验证登录态..." />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    element: <RequireAdmin />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'dashboard', element: <DashboardPage /> },
          { path: 'approvals', element: <ApprovalsPage /> },
          { path: 'users', element: <UsersPage /> },
          { path: 'customers', element: <CustomersPage /> },
          { path: 'order-notes', element: <OrderNotesPage /> },
          { path: 'order-history', element: <OrderHistoryPage /> },
          { path: 'membership', element: <MembershipPage /> },
          { path: 'payment', element: <PaymentPage /> },
          { path: 'payment-transfers', element: <PaymentTransfersPage /> },
          { path: 'referrals', element: <ReferralsPage /> },
          { path: 'discount-coupons', element: <DiscountCouponsPage /> },
          { path: 'promotion-links', element: <PromotionLinksPage /> },
          { path: 'phone-releases', element: <PhoneReleasesPage /> },
          { path: 'identity-merges', element: <IdentityMergesPage /> },
          { path: 'annual-archive', element: <AnnualArchivePage /> },
          { path: 'file-cleanup', element: <FileCleanupPage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'settings/register-flow', element: <RegisterFlowPage /> },
          { path: 'settings/credentials', element: <CredentialsPage /> },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);

function App() {
  const _hydrate = useAuthStore((s) => s._hydrate);

  useEffect(() => {
    void _hydrate();
  }, [_hydrate]);

  return (
    <ConfigProvider locale={zhCN} theme={antdTheme}>
      <AntApp>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  );
}

export default App;